import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chatApi } from '@/api/chat'
import { Sheet } from '@/components/ui/Sheet'
import { cn, relativeTime } from '@/lib/utils'
import { useChatStore } from '@/store/chat'
import type { ChatMessage, ChatTool, PendingAction } from '@/types/api'

import { ActionCard } from './ActionCard'
import { Composer } from './Composer'
import { Markdown } from './Markdown'
import { CheckIcon, CloseIcon, CrossIcon, HistoryIcon, PlusIcon, SparkleIcon, TrashIcon } from './icons'
import { useChat, type Draft } from './useChat'
import { useMediaQuery } from './useMediaQuery'

const SUGGESTIONS = ['What needs me today?', 'Show my top matches', 'Apply to my top 5 matches', 'Where are my applications?']

/**
 * The assistant. Docked as a right column on wide screens, a right drawer
 * on tablets, a near-full-screen bottom sheet on phones. It reads the same
 * data as the pages and, once the user says so, acts on it.
 */
export function ChatPanel() {
  const open = useChatStore((state) => state.open)
  const setOpen = useChatStore((state) => state.setOpen)
  const wide = useMediaQuery('(min-width: 1024px)')
  const medium = useMediaQuery('(min-width: 768px)')
  const close = () => setOpen(false)

  if (wide) {
    if (!open) return null
    return (
      <aside
        className="sticky hidden w-[380px] shrink-0 border-l border-line bg-surface lg:flex"
        style={{ top: 'calc(3.5rem + var(--safe-top))', height: 'calc(100dvh - 3.5rem - var(--safe-top))' }}
        aria-label="Assistant"
        data-testid="chat-panel"
      >
        <PanelBody mode="docked" onClose={close} />
      </aside>
    )
  }

  if (medium) {
    return (
      <Sheet open={open} onClose={close} side="right" size="sm" dismissible={false}>
        <div className="-mx-5 -mb-4 h-full" data-testid="chat-panel">
          <PanelBody mode="drawer" onClose={close} />
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet open={open} onClose={close} side="bottom" dismissible={false}>
      <div className="-mx-5 -mb-4 h-[calc(92dvh-28px)]" data-testid="chat-panel">
        <PanelBody mode="sheet" onClose={close} />
      </div>
    </Sheet>
  )
}

type Mode = 'docked' | 'drawer' | 'sheet'

function PanelBody({ mode, onClose }: { mode: Mode; onClose: () => void }) {
  const chat = useChat()
  const queryClient = useQueryClient()
  const [threadsOpen, setThreadsOpen] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)

  const threads = useQuery({
    queryKey: ['chat-threads'],
    queryFn: chatApi.threads,
    enabled: threadsOpen || Boolean(chat.threadId),
    staleTime: 30_000,
  })
  const title = threads.data?.find((entry) => entry.id === chat.threadId)?.title ?? chat.thread?.title ?? (chat.threadId ? 'Chat' : 'New chat')

  // Escape, with focus in the panel: closes the thread list, then (as an overlay) the panel.
  // A docked panel stays; it is part of the page, not a dialog.
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const active = document.activeElement
      if (active && active !== document.body && !root.current?.contains(active)) return
      if (threadsOpen) setThreadsOpen(false)
      else if (mode !== 'docked') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [threadsOpen, onClose, mode])

  // Follow the conversation as it grows unless the user has scrolled up to read.
  const stickToBottom = useRef(true)
  const onScroll = () => {
    const node = scroller.current
    if (!node) return
    stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80
  }
  useLayoutEffect(() => {
    const node = scroller.current
    if (node && stickToBottom.current) node.scrollTop = node.scrollHeight
  }, [chat.messages, chat.draft, chat.failure, chat.streamError])
  useLayoutEffect(() => {
    stickToBottom.current = true
    const node = scroller.current
    if (node) node.scrollTop = node.scrollHeight
  }, [chat.threadId])

  const remove = useMutation({
    mutationFn: (id: string) => chatApi.deleteThread(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ['chat-thread', id] })
      void queryClient.invalidateQueries({ queryKey: ['chat-threads'] })
      if (id === chat.threadId) chat.switchThread(null)
    },
  })

  const pick = (id: string | null) => {
    chat.switchThread(id)
    setThreadsOpen(false)
  }

  const empty = chat.messages.length === 0 && !chat.draft && !chat.loading
  const busy = Boolean(chat.confirming)
  const onNavigate = mode === 'docked' ? undefined : onClose
  const hover = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches

  return (
    <div ref={root} className="relative flex h-full min-h-0 flex-col bg-surface">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line pl-4 pr-1.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-soft text-brand" aria-hidden="true">
          <SparkleIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold leading-tight text-ink">Assistant</h2>
          <p className="truncate text-[12px] leading-tight text-ink-muted">{title}</p>
        </div>
        <IconButton label="New chat" onClick={() => pick(null)}>
          <PlusIcon className="h-5 w-5" />
        </IconButton>
        <IconButton label="Recent chats" active={threadsOpen} expanded={threadsOpen} onClick={() => setThreadsOpen((value) => !value)}>
          <HistoryIcon className="h-5 w-5" />
        </IconButton>
        <IconButton label="Close assistant" onClick={onClose}>
          <CloseIcon className="h-5 w-5" />
        </IconButton>
      </header>

      {threadsOpen && (
        <div className="absolute inset-0 z-10 flex flex-col" role="presentation">
          <div className="absolute inset-0 animate-fade-in bg-ink/20" onClick={() => setThreadsOpen(false)} />
          <div role="dialog" aria-label="Recent chats" className="relative mt-14 max-h-[60%] animate-fade-in overflow-y-auto rounded-b-card border-b border-line bg-surface shadow-float">
            <p className="label-eyebrow px-4 pb-1 pt-3">Recent chats</p>
            {threads.isLoading && <p className="px-4 py-3 text-[13px] text-ink-muted">Loading…</p>}
            {threads.isError && <p className="px-4 py-3 text-[13px] text-danger">Could not load your chats</p>}
            {threads.data?.length === 0 && <p className="px-4 pb-4 pt-2 text-[13px] text-ink-muted">No chats yet.</p>}
            <ul className="pb-2">
              {threads.data?.map((entry) => (
                <li key={entry.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => pick(entry.id)}
                    className={cn(
                      'flex min-h-[44px] min-w-0 flex-1 flex-col justify-center rounded-r-field pl-4 pr-2 text-left transition-colors hover:bg-surface-muted',
                      entry.id === chat.threadId && 'bg-accent-soft',
                    )}
                  >
                    <span className="block truncate text-[13.5px] font-medium text-ink">{entry.title || 'Untitled chat'}</span>
                    <span className="block text-[11.5px] text-ink-muted">{relativeTime(entry.updatedAt)}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove.mutate(entry.id)}
                    disabled={remove.isPending}
                    aria-label={`Delete chat ${entry.title || ''}`.trim()}
                    className="mr-1.5 grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-faint transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                  >
                    <TrashIcon className="h-[18px] w-[18px]" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4" role="log" aria-live="polite">
        {chat.loading && <p className="py-6 text-center text-[13px] text-ink-muted">Loading…</p>}
        {empty && <EmptyState onPick={(text) => void chat.send(text)} />}
        <div className="space-y-4">
          {chat.messages.map((message) => (
            <MessageView
              key={message.id}
              message={message}
              confirming={chat.confirming}
              onConfirm={(action) => void chat.confirm(action)}
              onDismiss={(action) => void chat.dismiss(action)}
              onNavigate={onNavigate}
            />
          ))}
          {chat.draft && <DraftView draft={chat.draft} confirming={chat.confirming} onNavigate={onNavigate} />}
          {chat.streamError && (
            <p className="flex items-start gap-2 text-[13px] leading-relaxed text-danger/80" role="alert">
              <CrossIcon className="mt-1 h-3.5 w-3.5 shrink-0" />
              <span>{chat.streamError}</span>
            </p>
          )}
          {chat.failure && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-muted" role="alert">
              <span>{chat.failure.message}</span>
              {chat.failure.retry && (
                <button type="button" onClick={chat.failure.retry} className="min-h-[32px] font-medium text-ink underline underline-offset-2">
                  Retry
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <Composer
        streaming={chat.streaming}
        disabled={busy || chat.loading}
        autoFocus={hover}
        onSend={(text) => void chat.send(text)}
        onStop={chat.stop}
        className="sticky bottom-0 shrink-0 border-t border-line bg-surface px-3 pt-3"
        style={{ paddingBottom: mode === 'sheet' ? 'calc(12px + var(--safe-bottom))' : '12px' }}
      />
    </div>
  )
}

function IconButton({ label, onClick, active, expanded, children }: { label: string; onClick: () => void; active?: boolean; expanded?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      className={cn(
        'grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink',
        active && 'bg-accent-soft text-ink',
      )}
    >
      {children}
    </button>
  )
}

function EmptyState({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div className="flex flex-col pb-4 pt-6">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-soft text-brand" aria-hidden="true">
        <SparkleIcon className="h-5 w-5" />
      </span>
      <h3 className="mt-3 text-[17px] font-semibold tracking-tight text-ink">How can I help?</h3>
      <p className="mt-1 text-[13.5px] leading-relaxed text-ink-muted">
        Ask about your matches and applications, or tell me what to do. I will check with you before anything is sent.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {SUGGESTIONS.map((text) => (
          <button
            key={text}
            type="button"
            onClick={() => onPick(text)}
            className="min-h-[44px] rounded-pill border border-line bg-surface px-4 text-left text-[13.5px] font-medium text-ink transition-colors hover:border-line-strong hover:bg-surface-muted"
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

function MessageView({
  message,
  confirming,
  onConfirm,
  onDismiss,
  onNavigate,
}: {
  message: ChatMessage
  confirming: string | null
  onConfirm: (action: PendingAction) => void
  onDismiss: (action: PendingAction) => void
  onNavigate?: () => void
}) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end" data-role="user">
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-[18px] rounded-br-[6px] bg-ink px-3.5 py-2 text-[14.5px] leading-relaxed text-oncolor">
          {message.content}
        </p>
      </div>
    )
  }
  return (
    <div data-role="assistant">
      {message.content.trim() && <Markdown content={message.content} onNavigate={onNavigate} />}
      {message.tools && message.tools.length > 0 && <ToolList tools={message.tools} />}
      {message.actions?.map((action) => (
        <ActionCard
          key={action.id}
          action={action}
          busy={confirming === action.id}
          disabled={Boolean(confirming) && confirming !== action.id}
          onConfirm={() => onConfirm(action)}
          onDismiss={() => onDismiss(action)}
        />
      ))}
    </div>
  )
}

/** The reply as it streams in: a pulsing dot until the final message replaces it. */
function DraftView({ draft, confirming, onNavigate }: { draft: Draft; confirming: string | null; onNavigate?: () => void }) {
  return (
    <div data-role="assistant" data-streaming="true">
      <div className="flex items-end gap-1.5">
        {draft.content.trim() ? <Markdown content={draft.content} onNavigate={onNavigate} className="min-w-0" /> : null}
        <span className="mb-1.5 inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand" aria-label="Thinking" />
      </div>
      {draft.tools.length > 0 && <ToolList tools={draft.tools} />}
      {draft.actions.map((action) => (
        <ActionCard key={action.id} action={action} busy={confirming === action.id} disabled onConfirm={() => {}} onDismiss={() => {}} />
      ))}
    </div>
  )
}

function ToolList({ tools, className }: { tools: ChatTool[]; className?: string }) {
  return (
    <ul className={cn('mt-2 space-y-1', className)}>
      {tools.map((tool) => (
        <li key={tool.id} className="flex items-center gap-2 text-[12.5px] text-ink-muted">
          {tool.status === 'started' && (
            <span className="relative grid h-3.5 w-3.5 shrink-0 place-items-center">
              <span className="absolute h-2 w-2 animate-ping rounded-full bg-brand opacity-60" />
              <span className="h-2 w-2 rounded-full bg-brand" />
            </span>
          )}
          {tool.status === 'done' && <CheckIcon className="h-3.5 w-3.5 shrink-0 text-success" />}
          {tool.status === 'failed' && <CrossIcon className="h-3.5 w-3.5 shrink-0 text-danger" />}
          <span className="truncate">{tool.status === 'started' ? tool.label : tool.summary || tool.label}</span>
        </li>
      ))}
    </ul>
  )
}
